import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  TextField,
  Button,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
  Paper,
} from "@mui/material";
import { getActivities, uploadImage } from "../../services/api";

const ADMIN_KEY = "65473";

export default function UploadImage() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ FILE STATE (IMPORTANT FIX)
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      activityId: "",
      key: "",
    },
  });

  // 🌿 Fetch Activities
  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const res = await getActivities();
        setActivities(res.data);
      } catch (error) {
        toast.error("Failed to load activities ❌");
      }
    };

    fetchActivities();
  }, []);

  // 🚀 Submit
  const onSubmit = async (data) => {
    if (data.key !== ADMIN_KEY) {
      toast.error("Only admin can upload images ❌");
      return;
    }

    if (!file) {
      toast.error("Please select an image 📸");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("image", file); // ✅ FIXED
      formData.append("activityId", data.activityId);

      setLoading(true);
      await uploadImage(formData);

      toast.success("Image Uploaded 📸");
      reset({
        activityId: "",
        key: "",
      });

      reset();
      setFile(null);
      setPreview(null);
    } catch (error) {
      console.error(error);
      toast.error("Upload failed ❌");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="min-h-screen flex items-center justify-center px-4">
      {/* 🌿 GLASS CARD */}
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "520px",
          p: 4,
          borderRadius: "24px",
          backdropFilter: "blur(20px)",
          background: "rgba(255,255,255,0.7)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
        }}
      >
        <Typography
          variant="h5"
          mb={3}
          textAlign="center"
          sx={{ color: "#065f46", fontWeight: "bold" }}
        >
          📸 Upload Activity Image
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
          {/* 🌱 Activity Select */}
          <TextField
            select
            label="Select Activity"
            fullWidth
            defaultValue=""
            {...register("activityId", { required: true })}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
              },
            }}
          >
            {activities.map((act) => (
              <MenuItem key={act._id} value={act._id}>
                {act.name}
              </MenuItem>
            ))}
          </TextField>

          {/* 📁 FILE INPUT */}
          <Box>
            <Typography mb={1} sx={{ color: "#065f46" }}>
              Upload Image
            </Typography>

            <label
              className="flex flex-col items-center justify-center p-6 rounded-2xl cursor-pointer transition hover:scale-[1.02]"
              style={{
                border: "2px dashed #22c55e",
                background: "linear-gradient(135deg, #ecfdf5, #d1fae5)",
              }}
            >
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  const selected = e.target.files[0];
                  if (selected) {
                    setFile(selected);
                    setPreview(URL.createObjectURL(selected));
                  }
                }}
              />

              {preview ? (
                <>
                  <img
                    src={preview}
                    alt="preview"
                    className="w-full max-h-52 object-cover rounded-xl shadow-md"
                  />
                  <p className="text-sm text-green-700 mt-2">
                    Image selected ✔
                  </p>
                </>
              ) : (
                <>
                  <div className="text-4xl mb-2">📸</div>
                  <p className="text-green-800 font-medium">
                    Click or Drag Image Here
                  </p>
                  <span className="text-sm text-gray-500">
                    JPG, PNG supported
                  </span>
                </>
              )}
            </label>
          </Box>

          {/* 🔑 ADMIN KEY */}
          <TextField
            label="Admin Key"
            type="password"
            fullWidth
            {...register("key", { required: true })}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
              },
            }}
          />

          <Button
            type="submit"
            fullWidth
            disabled={loading}
            sx={{
              py: 1.6,
              borderRadius: "16px",
              fontWeight: "bold",
              fontSize: "16px",
              color: "#ffffff", // ✅ FIX TEXT COLOR

              background: "linear-gradient(135deg, #22c55e, #4ade80, #16a34a)",

              boxShadow: "0 12px 35px rgba(34,197,94,0.4)",

              "&:hover": {
                background: "linear-gradient(135deg, #16a34a, #15803d)",
              },

              "&:disabled": {
                background: "#a7f3d0",
                color: "#065f46",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "🌿 Upload Image"
            )}
          </Button>
        </form>
      </Paper>
    </Box>
  );
}